/**
 * Semantic visual roles only. Do not add feature-local color copies.
 *
 * Surface ladder (dark → light): canvas, container, raised, popover,
 * interactive, control. Clickable chrome on a panel uses `control`, never the
 * parent panel fill. Action recipes live in `src/shared/styles/action.styles.ts`.
 */

const surface = {
  canvas: "#0d0f11",
  container: "#16191a",
  raised: "#1d1d1d",
  popover: "#222222",
  interactive: "#282828",
  subtle: "#1c1e1f",
  subtleHover: "#2c2f31",
  subtleSelected: "#4c5154",
  control: "#1f2223",
  controlHover: "#282b2d",
} as const;

const text = {
  primary: "#ffffff",
  secondary: "#646464",
  muted: "#999999",
  link: "#1c7995",
  inverse: "#394246",
} as const;

const border = {
  black: "#000000",
  default: "#23272a",
  subtle: "#292929",
  popover: "#111111",
} as const;

const action = {
  primary: "#18a86b",
  primaryHover: "#50d5a5",
  primaryPressed: "#0f613f",
  secondary: "#0b679f",
  secondaryHover: "#1976d2",
  danger: "#9f0b0b",
  dangerHover: "#c65050",
  disabled: "#4c5154",
} as const;

const status = {
  success: action.primary,
  danger: action.dangerHover,
  warning: "#efb221",
  info: "#64b5f6",
  special: "#743699",
} as const;

const overlay = {
  transparent: "#00000000",
  scrim: "#000000b0",
  white10: "rgba(255, 255, 255, 0.1)",
  white20: "rgba(255, 255, 255, 0.2)",
} as const;

export const designTokens = {
  color: {
    surface,
    text,
    border,
    action,
    status,
    overlay,
  },
  gradient: {
    action: {
      primary:
        "linear-gradient(180deg, rgba(0, 173, 115, 1) 0%, rgba(17, 179, 132, 1) 100%)",
      create:
        "linear-gradient(135deg, rgba(37, 58, 75, 1) 0%, rgba(80, 125, 160, 1) 100%)",
      danger:
        "linear-gradient(135deg, rgba(255, 136, 124, 1) 0%, rgba(182, 50, 95, 1) 100%)",
      warning: "linear-gradient(135deg, #fab17b 0%, #f6c76d 100%)",
      info:
        "linear-gradient(135deg, rgba(8, 196, 156, 1) 0%, rgba(0, 138, 185, 1) 100%)",
      neutral:
        "linear-gradient(135deg, rgba(74, 74, 74, 1) 0%, rgba(51, 51, 51, 1) 100%)",
    },
    text: {
      reward: "linear-gradient(90deg, #caf881 0%, #a0e99a 100%)",
      gold:
        "linear-gradient(135deg, rgba(108, 101, 4, 1) 0%, rgba(212, 210, 155, 1) 50%, rgba(108, 101, 4, 1) 100%)",
      special: "linear-gradient(0deg, #8b2ded 30%, #eb17e6 80%)",
      medallion:
        "linear-gradient(180deg, #fbe7a1 0%, #e8bd5a 34%, #c48f2c 58%, #8f6118 82%, #c9952f 100%)",
      medallionShine:
        "linear-gradient(105deg, transparent 42%, rgba(255, 248, 220, 0.85) 50%, transparent 58%)",
    },
    surface: {
      shimmer:
        "linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.05) 100%)",
      fadeBottom: "linear-gradient(to bottom, black 60%, transparent 100%)",
      fadeRight: "linear-gradient(to right, black 60%, transparent 100%)",
      medallionHalo:
        "radial-gradient(closest-side, rgba(232, 189, 90, 0.28) 0%, rgba(232, 189, 90, 0.08) 55%, transparent 100%)",
    },
  },
  brand: {
    membership: {
      standard: "#4d4d4d",
      plus:
        "linear-gradient(180deg, rgba(201, 156, 38, 1) 0%, rgba(201, 156, 38, 1) 49%, rgba(244, 243, 171, 1) 50%, rgba(242, 236, 137, 1) 100%)",
      unlimited:
        "linear-gradient(180deg, rgba(82, 16, 124, 1) 0%, rgba(206, 20, 130, 1) 100%)",
    },
    qualification: {
      qualified:
        "linear-gradient(135deg, #3950a1 0%, #379ac2 49%, #42cdb1 100%)",
      qualifiedGlow: "#42cdb1 0 0 20px -6px",
      unavailable: surface.popover,
      unavailableGlow: `${action.dangerHover} 0 0 32px -6px`,
    },
    header: {
      rankTrack: surface.canvas,
      rankProgress: "linear-gradient(45deg, rgb(180, 96, 240), #5808fb)",
      rankTextShadow: "0 0 2px rgba(0, 0, 0, 0.6)",
    },
    perk: {
      success: "#af37e1",
      cooldown: "#69dd77",
      booster: "#dabd63",
      tools: "#609fd9",
    },
    profileTier: {
      common: "#70c39b",
      rare: "#8dbfe5",
      mythic: "#d9ace6",
    },
    market: {
      seller: "#794c32",
      buyer: "#578635",
    },
    roulette: {
      red: "#c62828",
      black: "#080808",
    },
    gambling: {
      felt: "#046d38",
      feltSelected: "rgb(14, 174, 180)",
    },
  },
  typography: {
    fontFamily: '"Philosopher", sans-serif',
    size: {
      caption: "0.75rem",
      bodySmall: "0.875rem",
      body: "1rem",
      titleSmall: "1.125rem",
      title: "1.5rem",
      display: "2rem",
    },
    lineHeight: {
      compact: 1.2,
      default: 1.5,
      relaxed: 1.7,
    },
  },
  space: {
    none: 0,
    xxs: 2,
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    "2xl": 32,
    "3xl": 48,
    "4xl": 64,
  },
  size: {
    control: { sm: 32, md: 40, lg: 48 },
    icon: { sm: 16, md: 20, lg: 24 },
    content: { narrow: 640, default: 960, wide: 1280, full: 1920 },
  },
  radius: {
    sm: 4,
    md: 8,
    lg: 12,
    xl: 16,
    pill: 999,
  },
  opacity: {
    disabled: 0.4,
    muted: 0.64,
    hover: 0.8,
    visible: 1,
  },
  shadow: {
    sm: "0 2px 8px rgba(0, 0, 0, 0.24)",
    md: "0 8px 24px rgba(0, 0, 0, 0.32)",
    lg: "0 16px 48px rgba(0, 0, 0, 0.4)",
    toast: "0 16px 40px rgba(0, 0, 0, 0.55)",
    dropdown: "0 4px 20px rgba(0, 0, 0, 0.5)",
    popover: "0 0 10px rgba(0, 0, 0, 0.5)",
    focus: "0 0 0 3px rgba(28, 121, 149, 0.35)",
    inset:
      "inset 0 -3px 10px rgb(0 0 0 / 15%), inset 3px 0 10px rgb(0 0 0 / 15%), inset 0 3px 10px rgb(0 0 0 / 15%), inset -3px 0 10px rgb(0 0 0 / 15%)",
    insetStrong:
      "inset 0 -3px 6px rgb(0 0 0 / 35%), inset 3px 0 6px rgb(0 0 0 / 35%), inset 0 3px 6px rgb(0 0 0 / 35%), inset -3px 0 6px rgb(0 0 0 / 35%)",
    successGlow: "0 0 20px rgba(24, 168, 107, 0.3)",
    dangerGlow: "0 0 20px rgba(198, 80, 80, 0.3)",
    medallion:
      "0 0 0 2px rgba(0, 0, 0, 0.6), 0 12px 32px rgba(0, 0, 0, 0.6), 0 0 56px rgba(232, 189, 90, 0.35)",
    medallionText: "0 6px 14px rgba(0, 0, 0, 0.7)",
  },
  motion: {
    duration: {
      instant: "100ms",
      fast: "160ms",
      normal: "240ms",
      slow: "360ms",
    },
    easing: {
      standard: "cubic-bezier(0.2, 0, 0, 1)",
      emphasized: "cubic-bezier(0.2, 0, 0, 1.2)",
      exit: "cubic-bezier(0.4, 0, 1, 1)",
    },
  },
  layer: {
    content: 1,
    sticky: 100,
    navigation: 200,
    dropdown: 1000,
    overlay: 1200,
    modal: 1300,
    toast: 1400,
    tooltip: 1500,
  },
  layout: {
    pageGutter: {
      mobile: 12,
      tablet: 20,
      desktop: 24,
    },
    sectionGap: {
      mobile: 16,
      desktop: 24,
    },
    sidebarWidth: 240,
    readableLineLength: "68ch",
  },
  breakpoint: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1400,
  },
} as const;

export type DesignTokens = typeof designTokens;
